import { usePdpLayout } from "@redux/slices/pdpLayoutSlice";
import { useSelector } from "react-redux";
import { useEffect, useState, useMemo } from "react";
import { PdpSections, SectionLayout } from "@utils/customization";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { PdpSectionConfig } from "@scenes/pdp/utils/pdpConfigurableComponent";

export const useGetPdpSectionLayout = (
  sectionName: string,
  selectedProductType?: string,
  stoneType: any = SearchFilterTypeEnum.SINGLE_STONES,
  user?: any
) => {
  const pdpLayout = usePdpLayout();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const productType = selectedProductType ? selectedProductType : activeProduct;
  const [sectionLayout, setSectionLayout] = useState<SectionLayout>(
    [] as SectionLayout
  );

  useEffect(() => {
    if (pdpLayout && typeof pdpLayout[productType] === "object") {
      let productLayout = pdpLayout[productType];
      switch (stoneType) {
        case SearchFilterTypeEnum.SINGLE_STONES:
          productLayout = pdpLayout[productType];
          break;
        case SearchFilterTypeEnum.PAIRS:
          productLayout =
            pdpLayout[productType]?.[SearchFilterTypeEnumLowercase.PAIRS] ??
            pdpLayout[productType];
          break;
        case SearchFilterTypeEnum.PARCEL:
          productLayout =
            pdpLayout[productType]?.parcels ?? pdpLayout[productType];
          break;
        default:
          productLayout = pdpLayout[productType];
          break;
      }
      if (sectionName && productLayout[sectionName]) {
        const targetSection = productLayout[sectionName];

        setSectionLayout(targetSection ? targetSection : null);
      }
    }
  }, [pdpLayout, productType, sectionName, user]);
  return sectionLayout;
};

export const useGetPdpSectionsOrder = (
  selectedProductType?: string,
  stoneType: any = SearchFilterTypeEnum.SINGLE_STONES
): PdpSectionConfig[] | undefined => {
  const pdpLayout = usePdpLayout();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const productType = selectedProductType ? selectedProductType : activeProduct;

  return useMemo(() => {
    if (!pdpLayout || typeof pdpLayout[productType] !== "object") {
      return undefined;
    }

    let productLayout = pdpLayout[productType];

    switch (stoneType) {
      case SearchFilterTypeEnum.PAIRS:
        productLayout =
          pdpLayout[productType]?.[SearchFilterTypeEnumLowercase.PAIRS] ??
          pdpLayout[productType];
        break;
      case SearchFilterTypeEnum.PARCEL:
        productLayout =
          pdpLayout[productType]?.parcels ?? pdpLayout[productType];
        break;
      default:
        productLayout = pdpLayout[productType];
        break;
    }

    const sectionOrderConfig = productLayout?.[PdpSections.PDP_SECTIONS_ORDER];

    if (sectionOrderConfig && Array.isArray(sectionOrderConfig.sections)) {
      return sectionOrderConfig.sections;
    }

    return undefined;
  }, [pdpLayout, productType, stoneType]);
};

export const useGetReviewPageSectionsOrder = (
  selectedProductType?: string
): PdpSectionConfig[] | undefined => {
  const pdpLayout = usePdpLayout();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const productType = selectedProductType ? selectedProductType : activeProduct;

  return useMemo(() => {
    if (!pdpLayout || typeof pdpLayout[productType] !== "object") {
      return undefined;
    }

    const reviewPageConfig = pdpLayout[productType]?.review_page;
    const sectionOrderConfig =
      reviewPageConfig?.[PdpSections.PDP_SECTIONS_ORDER];

    if (sectionOrderConfig && Array.isArray(sectionOrderConfig.sections)) {
      return sectionOrderConfig.sections;
    }

    return undefined;
  }, [pdpLayout, productType]);
};

export const useGetFindMatchSectionsOrder = (
  selectedProductType?: string
): PdpSectionConfig[] | undefined => {
  const pdpLayout = usePdpLayout();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const productType = selectedProductType ? selectedProductType : activeProduct;

  return useMemo(() => {
    if (!pdpLayout || typeof pdpLayout[productType] !== "object") {
      return undefined;
    }

    const findMatchConfig = pdpLayout[productType]?.find_match;
    const sectionOrderConfig =
      findMatchConfig?.[PdpSections.PDP_SECTIONS_ORDER];

    if (sectionOrderConfig && Array.isArray(sectionOrderConfig.sections)) {
      return sectionOrderConfig.sections;
    }

    return undefined;
  }, [pdpLayout, productType]);
};
